// V3.0 更新：对齐后端实际字段值
// 后端 status 字段的实际值（处理状态）
// V4.1 更新：根据业务规则，只有 COMPLETED 和 REJECTED 两种最终状态
export const ActionItemStatus = Object.freeze({
    PENDING_AI: "PENDING_AI", // AI处理中
    PENDING_REVIEW: "PENDING_REVIEW", // 待人工审核
    PROCESSING: "PROCESSING", // 执行中
    COMPLETED: "COMPLETED", // 已完成
    FAILED: "FAILED", // 执行失败
    REJECTED: "REJECTED", // 已拒绝
});

// 后端 resolution 字段的实际值（解决状态）
export const ActionItemResolution = Object.freeze({
    AUTO_EXECUTED: "AUTO_EXECUTED", // 系统自动执行
    USER_CONFIRMED: "USER_CONFIRMED", // 用户确认
    USER_MODIFIED: "USER_MODIFIED", // 用户修改后确认
    USER_REJECTED: "USER_REJECTED", // 用户拒绝
});

// 兼容性：保留旧常量名，但映射到新值（逐步迁移用）
export const ProcessingStage = Object.freeze({
    PENDING_AI: ActionItemStatus.PENDING_AI,
    PENDING_REVIEW: ActionItemStatus.PENDING_REVIEW,
    PROCESSING: ActionItemStatus.PROCESSING,
    COMPLETED: ActionItemStatus.COMPLETED,
    FAILED: ActionItemStatus.FAILED,
    REJECTED: ActionItemStatus.REJECTED,
    // 旧值映射（用于向后兼容，但应逐步替换）
    PENDING_MANUAL_AI: ActionItemStatus.PENDING_REVIEW,
    AUTO_SUCCESS: ActionItemStatus.COMPLETED,
    USER_SUCCESS: ActionItemStatus.COMPLETED,
    CLOSED_FAILED: ActionItemStatus.FAILED,
});

export const ResolutionStatus = Object.freeze({
    AUTO_EXECUTED: ActionItemResolution.AUTO_EXECUTED,
    USER_CONFIRMED: ActionItemResolution.USER_CONFIRMED,
    USER_MODIFIED: ActionItemResolution.USER_MODIFIED,
    USER_REJECTED: ActionItemResolution.USER_REJECTED,
    // 旧值映射（用于向后兼容）
    AUTO_RESOLVED: ActionItemResolution.AUTO_EXECUTED,
    USER_MODIFIED_CONFIRMED: ActionItemResolution.USER_MODIFIED,
});

// Actual SourceType values (keys and values are the same for direct comparison)
export const SourceType = Object.freeze({
    FLIGHT_MESSAGE_MVT: "FLIGHT_MESSAGE_MVT",
    FLIGHT_TELEGRAM_OTHER: "FLIGHT_TELEGRAM_OTHER",
    SYSTEM_FLIGHT_CONFLICT_ALERT: "SYSTEM_FLIGHT_CONFLICT_ALERT",
    SYSTEM_SCHEDULE_ALERT: "SYSTEM_SCHEDULE_ALERT",
    USER_INPUT_REQUEST: "USER_INPUT_REQUEST",
    CONTRACT_EXPIRY_WARNING: "CONTRACT_EXPIRY_WARNING", // 合同到期预警
    CATERING_PROCESSING: "CATERING_PROCESSING", // 餐食处理代理
    SETTLEMENT_PROCESSING: "SETTLEMENT_PROCESSING", // 结算处理代理
});

// Actual ActionType values (keys and values are the same for direct comparison)
// 这些值对应后端的suggested_change_type字段
export const ActionType = Object.freeze({
    UPDATE_OPERATION_FLIGHT_FROM_MVT: "UPDATE_OPERATION_FLIGHT_FROM_MVT",
    UPDATE_AIRCRAFT_REGISTRATION: "UPDATE_AIRCRAFT_REGISTRATION",
    CHANGE_FLIGHT_STATUS: "CHANGE_FLIGHT_STATUS",
    GENERATE_FLIGHT_ALERT: "GENERATE_FLIGHT_ALERT",
    RESOLVE_CONFLICT: "RESOLVE_CONFLICT",
    // Add other action types as they become relevant
});

export const ActionItemEventType = Object.freeze({
    CREATED: "ACTION_ITEM_CREATED",
    AI_PROCESSING_STARTED: "ACTION_ITEM_AI_PROCESSING_STARTED",
    AI_PROCESSING_FAILED: "ACTION_ITEM_AI_PROCESSING_FAILED",
    PENDING_REVIEW_AI: "ACTION_ITEM_PENDING_REVIEW_AI_ASSISTED",
    PENDING_REVIEW_RAW: "ACTION_ITEM_PENDING_REVIEW_RAW_INPUT",
    AUTO_CONFIRMED: "ACTION_ITEM_AUTO_CONFIRMED",
    USER_CONFIRMED: "ACTION_ITEM_USER_CONFIRMD", // Typo fixed from previous version
    USER_MODIFIED: "ACTION_ITEM_USER_MODIFIED",
    USER_REJECTED: "ACTION_ITEM_USER_REJECTED",
    EXECUTION_FAILED: "ACTION_ITEM_EXECUTION_FAILED",
});

export const SOURCE_TYPE_LABELS = Object.freeze({
    [SourceType.FLIGHT_MESSAGE_MVT]: "MVT报文",
    [SourceType.FLIGHT_TELEGRAM_OTHER]: "其他电报",
    [SourceType.SYSTEM_FLIGHT_CONFLICT_ALERT]: "航班冲突警告",
    [SourceType.SYSTEM_SCHEDULE_ALERT]: "计划调度告警",
    [SourceType.USER_INPUT_REQUEST]: "用户输入请求",
    [SourceType.CONTRACT_EXPIRY_WARNING]: "合同到期预警", // 合同到期预警标签
    [SourceType.CATERING_PROCESSING]: "餐食处理", // 餐食处理代理标签
    [SourceType.SETTLEMENT_PROCESSING]: "结算处理", // 结算处理代理标签
});

export const ACTION_TYPE_LABELS = Object.freeze({
    [ActionType.UPDATE_OPERATION_FLIGHT_FROM_MVT]: "航班时刻更新 (MVT)",
    [ActionType.UPDATE_AIRCRAFT_REGISTRATION]: "更新飞机注册号",
    [ActionType.CHANGE_FLIGHT_STATUS]: "变更航班状态",
    [ActionType.GENERATE_FLIGHT_ALERT]: "生成航班告警",
    [ActionType.RESOLVE_CONFLICT]: "解决冲突",
});

// V3.0 更新：基于新的状态字段的标签映射
export const ACTION_ITEM_STATUS_INFO = Object.freeze({
    [ActionItemStatus.PENDING_AI]: { label: "AI分析中", color: "processing" },
    [ActionItemStatus.PENDING_REVIEW]: { label: "待审核", color: "warning" },
    [ActionItemStatus.PROCESSING]: { label: "执行中", color: "processing" },
    [ActionItemStatus.COMPLETED]: { label: "已完成", color: "success" },
    [ActionItemStatus.FAILED]: { label: "执行失败", color: "error" },
    [ActionItemStatus.REJECTED]: { label: "已拒绝", color: "volcano" },
});

export const ACTION_ITEM_RESOLUTION_INFO = Object.freeze({
    [ActionItemResolution.AUTO_EXECUTED]: { label: "系统自动执行", color: "green" },
    [ActionItemResolution.USER_CONFIRMED]: { label: "用户已确认", color: "success" },
    [ActionItemResolution.USER_MODIFIED]: { label: "用户修改后确认", color: "success" },
    [ActionItemResolution.USER_REJECTED]: { label: "用户已拒绝", color: "volcano" },
    // 新增实际后端返回的resolution值
    CONFIRMED_AUTO: { label: "自动确认", color: "success" },
    CONFIRMED_USER: { label: "用户确认", color: "success" },
    MODIFIED_USER: { label: "用户修改", color: "success" },
    REJECTED_USER: { label: "用户拒绝", color: "volcano" },
});

// 兼容性：保留旧的标签映射（逐步迁移用）
export const PROCESSING_STAGE_INFO = Object.freeze({
    [ActionItemStatus.PENDING_AI]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.PENDING_AI],
    [ActionItemStatus.PENDING_REVIEW]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.PENDING_REVIEW],
    [ActionItemStatus.PROCESSING]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.PROCESSING],
    [ActionItemStatus.COMPLETED]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.COMPLETED],
    [ActionItemStatus.FAILED]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.FAILED],
    [ActionItemStatus.REJECTED]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.REJECTED],
    // 旧值兼容
    [ProcessingStage.PENDING_MANUAL_AI]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.PENDING_REVIEW],
    [ProcessingStage.AUTO_SUCCESS]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.COMPLETED],
    [ProcessingStage.USER_SUCCESS]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.COMPLETED],
    [ProcessingStage.CLOSED_FAILED]: ACTION_ITEM_STATUS_INFO[ActionItemStatus.FAILED],
});

export const RESOLUTION_STATUS_LABELS = Object.freeze({
    [ActionItemResolution.AUTO_EXECUTED]: "系统自动执行",
    [ActionItemResolution.USER_CONFIRMED]: "用户已确认",
    [ActionItemResolution.USER_MODIFIED]: "用户修改后确认",
    [ActionItemResolution.USER_REJECTED]: "用户已拒绝",
    // 旧值兼容
    [ResolutionStatus.AUTO_RESOLVED]: "系统自动执行",
    [ResolutionStatus.USER_CONFIRMED]: "用户已确认",
    [ResolutionStatus.USER_MODIFIED]: "用户修改后确认",
    [ResolutionStatus.USER_REJECTED]: "用户已拒绝",
});

export const RESOLUTION_STATUS_INFO = Object.freeze({
    [ActionItemResolution.AUTO_EXECUTED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.AUTO_EXECUTED],
    [ActionItemResolution.USER_CONFIRMED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_CONFIRMED],
    [ActionItemResolution.USER_MODIFIED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_MODIFIED],
    [ActionItemResolution.USER_REJECTED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_REJECTED],
    // 旧值兼容
    [ResolutionStatus.AUTO_RESOLVED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.AUTO_EXECUTED],
    [ResolutionStatus.USER_CONFIRMED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_CONFIRMED],
    [ResolutionStatus.USER_MODIFIED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_MODIFIED],
    [ResolutionStatus.USER_REJECTED]: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_REJECTED],
});

export const PRIORITY_INFO = Object.freeze({
    1: { label: "高", color: "error" },
    2: { label: "中", color: "warning" },
    3: { label: "低", color: "default" },
});

// V3.0 更新：分离状态筛选和解决状态筛选
// 处理状态筛选选项（用于 status 参数）
export const STATUS_FILTER_OPTIONS = Object.freeze([
    { value: ActionItemStatus.COMPLETED, label: ACTION_ITEM_STATUS_INFO[ActionItemStatus.COMPLETED].label },
    { value: ActionItemStatus.FAILED, label: ACTION_ITEM_STATUS_INFO[ActionItemStatus.FAILED].label },
    { value: ActionItemStatus.REJECTED, label: ACTION_ITEM_STATUS_INFO[ActionItemStatus.REJECTED].label },
]);

// 解决状态筛选选项（用于 resolution 参数）
export const RESOLUTION_FILTER_OPTIONS = Object.freeze([
    { value: ActionItemResolution.AUTO_EXECUTED, label: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.AUTO_EXECUTED].label },
    { value: ActionItemResolution.USER_CONFIRMED, label: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_CONFIRMED].label },
    { value: ActionItemResolution.USER_MODIFIED, label: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_MODIFIED].label },
    { value: ActionItemResolution.USER_REJECTED, label: ACTION_ITEM_RESOLUTION_INFO[ActionItemResolution.USER_REJECTED].label },
]);

// 兼容性：保留旧的历史筛选选项（但改为使用新的查询逻辑）
export const HISTORY_STATUS_FILTER_OPTIONS = Object.freeze([
    { value: "ALL_RESOLVED", label: "全部已处理 (汇总)" },
    {
        label: "按处理状态",
        options: STATUS_FILTER_OPTIONS,
    },
    {
        label: "按解决状态",
        options: RESOLUTION_FILTER_OPTIONS,
    },
]);

// Options for NotificationCenterPage source_type filter
export const SOURCE_TYPE_FILTER_OPTIONS = Object.entries(SOURCE_TYPE_LABELS).map(([key, label]) => ({
    value: key,
    label: label,
}));

// Options for NotificationCenterPage action_type filter
export const ACTION_TYPE_FILTER_OPTIONS = Object.entries(ACTION_TYPE_LABELS).map(([key, label]) => ({
    value: key,
    label: label,
}));

// Options for NotificationCenterPage priority filter (pending tab)
export const PRIORITY_FILTER_OPTIONS = Object.entries(PRIORITY_INFO).map(([key, info]) => ({
    value: key,
    label: info.label,
}));
